import { graphicApi } from '@/api';
import { Modal } from '@/components/common';
import { WarningSign } from '@/components/graphic/MenuBar/Icons';
import {
	buildColumn,
	getDefaultPaginationSettings,
	getDefaultTablePaginationConfig,
	getFullVelocityUrl,
	handleResponse,
	removeCommasAndPercentSign,
	ScrollType,
	useHandleOnChangeTableLogic,
} from '@/Helper';
import { getPermissionErrorMessage, SubPermissions, User } from '@/model/AccountModel';
import { DefaultColumns, DefaultGridElement, PaginationSetting } from '@/model/CommonModel';
import { DataTableType } from '@/model/DeviceAdminModel';
import { BadgeDirectoryInfo, GraphicType } from '@/model/GraphicModel';
import { Logger } from '@/model/LoggingModel';
import { Input, Spin, Table, TablePaginationConfig } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { SorterResult } from 'antd/lib/table/interface';
import debounce from 'lodash.debounce';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { batch } from 'react-redux';
import styles from './badgeDirectoryModal.module.scss';

type Props = {
	onCancel: () => void;
	handleOnOpen: (data: Partial<BadgeDirectoryInfo>) => Promise<void>;
};

const scroll: ScrollType = { y: 300 };

const closeIconStyle: React.CSSProperties = { color: 'white', fontSize: '16px' };
const maskStyle: React.CSSProperties = { backgroundColor: '#dcdcdcb8' };

type DataType = DataTableType<BadgeDirectoryInfo>;
type BadgeDirectoryInfoTable = BadgeDirectoryInfo & DefaultColumns & Partial<DefaultGridElement>;

const { Search } = Input;

const user: User = getUser();
const graphicsComponentPermission = User.getSubComponentPermission(user, SubPermissions.Graphics_Graphics_Use);
const hasCorePermission = graphicsComponentPermission.allowed && user.isAdmin;
const warningStyle: React.CSSProperties = { width: '30px', height: '27px' };

const badgeFolderImagePath = `${getFullVelocityUrl()}graphic/content/images/Badge_Folder.svg?_=${getScriptVersion()}`;

const BadgeDirectoryModal: React.FC<Props> = ({ onCancel, handleOnOpen }) => {
	//#region State Management Variables
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [badgeDirectoryData, setBadgeDirectoryData] = useState<BadgeDirectoryInfoTable[]>([]);
	const [paginationSetting, setPaginationSetting] = useState<PaginationSetting>(getDefaultPaginationSettings());
	const [searchText, setSearchText] = useState<string>('');
	const isSearchPerformed = useRef<boolean>(false);
	const isTableChangePerformed = useRef<boolean>(false);
	const isPaginationChangedPerformed = useRef<boolean>(false);
	const handleOnChangeTableLogic = useHandleOnChangeTableLogic();
	//#endregion

	useEffect(() => {
		fetchBadgeTemplateData(paginationSetting);
	}, []);

	const fetchBadgeTemplateData = (pagination: PaginationSetting, isSearch: boolean = false, isPaginationChanged: boolean = false) => {
		setIsLoading(true);
		graphicApi
			.getBadgeDirectoryBy(pagination)
			.then(response => {
				if (!handleResponse(response)) {
					const { BadgeDirectoryInfo, TotalItemsPaginated } = response.Entity;
					batch(() => {
						setPaginationSetting({
							...pagination,
							TotalItems: TotalItemsPaginated,
						});
						setBadgeDirectoryData(createTableData(BadgeDirectoryInfo));
					});
				}
			})
			.catch(e => {
				Logger.writeErrorLog(e);
			})
			.finally(() => {
				setIsLoading(false);
				if (isSearch) {
					isSearchPerformed.current = false;
				}
				if (isPaginationChanged) {
					isPaginationChangedPerformed.current = false;
				}
			});
	};

	const createTableData = (data: BadgeDirectoryInfo[]): BadgeDirectoryInfoTable[] => {
		if (data?.length > 0) {
			return data.map<BadgeDirectoryInfoTable>(m => ({
				...m,
				Action: undefined,
				key: m.Id,
				LastUpdated: FormatDate(m.LastUpdated, false, null, true),
			}));
		}
		return [];
	};

	const callSearch = useCallback(
		debounce(async (v: string) => {
			const tempPagination: PaginationSetting = { ...paginationSetting, PageNumber: 1, SearchedValue: v };
			fetchBadgeTemplateData(tempPagination, true);
		}, 500),
		[paginationSetting?.SearchedValue]
	);

	const handleOnSetSearchText = (e: React.ChangeEvent<HTMLInputElement>) => {
		isSearchPerformed.current = true;
		const { value } = e.currentTarget;
		const st = removeCommasAndPercentSign(value);
		setSearchText(st);
		callSearch(st);
	};

	const handleOnOpenBadgeDirectory = (badge: BadgeDirectoryInfoTable): void => {
		setIsLoading(true);
		handleOnOpen({ Id: badge.Id, Name: badge.Name, Type: GraphicType.VelocityCore }).then(() => {
			setIsLoading(false);
		});
	};

	const columns: ColumnsType<DataType> = [
		{
			...buildColumn(_('Name'), 'Name', 'auto', 'start'),
			sorter: true,
		},
		{
			...buildColumn(_('LastUpdated'), 'LastUpdated', '150px', 'start'),
			sorter: true,
		},
	];

	const handleChangePagination = (page: number, pageSize: number) => {
		if (isTableChangePerformed.current || isPaginationChangedPerformed.current) return;
		isPaginationChangedPerformed.current = true;
		fetchBadgeTemplateData(
			{
				...paginationSetting,
				PageNumber: page,
				PageSize: pageSize,
			},
			false,
			true
		);
	};

	const clearSelection = () => {};
	const handleOnChangeTable = (pagination: TablePaginationConfig, filters: { Name?: string[] }, sorter: SorterResult<DataType>) => {
		if (isSearchPerformed.current || isTableChangePerformed.current || isPaginationChangedPerformed.current) return;
		isTableChangePerformed.current = true;
		const { current, pageSize, shouldUpdateSearchResults, shouldUpdateTableResults, sortField, sortOrder } = handleOnChangeTableLogic({
			clearSelection,
			filters,
			handleChangePagination: handleChangePagination,
			isSearchPerformed: isSearchPerformed,
			pagination,
			shouldResetSearchColumn: false,
			sorter,
			tablePaginationSetting: paginationSetting,
		});

		if (shouldUpdateTableResults || shouldUpdateSearchResults) {
			fetchBadgeTemplateData({
				...paginationSetting,
				PageNumber: current,
				PageSize: pageSize,
				SortDirection: sortOrder,
				SortField: sortField?.toString(),
			});
		}
		isTableChangePerformed.current = false;
	};

	const tablePagination: TablePaginationConfig = getDefaultTablePaginationConfig(
		false,
		handleChangePagination,
		paginationSetting.PageNumber,
		paginationSetting.PageSize,
		paginationSetting.TotalItems,
		undefined,
		[]
	);

	const content = (
		<>
			<div className={styles.searchContainer}>
				<Search
					allowClear
					className={styles.search}
					bordered={false}
					maxLength={50}
					placeholder={_('FilterByName')}
					value={searchText}
					onChange={handleOnSetSearchText}
				/>
			</div>
			<br />
			<Table
				id='badgeDirectoryModalTable'
				key={'badgeDirectoryModalKey'}
				className={styles.badgeModalTable}
				size='small'
				dataSource={badgeDirectoryData}
				columns={columns}
				onRow={
					hasCorePermission
						? (row: BadgeDirectoryInfoTable) => {
								return {
									onClick: () => {
										handleOnOpenBadgeDirectory(row);
									},
									title: getPermissionErrorMessage(hasCorePermission),
								};
						  }
						: null
				}
				onChange={handleOnChangeTable}
				pagination={tablePagination}
				scroll={scroll}
			/>
		</>
	);

	const footer: React.ReactNode = (
		<div key='warning' className={styles.warningMessage}>
			<div>
				<div className={styles.warningIcon}>
					<WarningSign elementStyle={warningStyle} />
				</div>
				<span className={styles.warningText}>
					{_('BadgeDirectoryWarning_1')}
					<br />
					{_('BadgeDirectoryWarning_2')}
				</span>
			</div>
		</div>
	);

	const title: React.ReactNode = (
		<div className={styles.titleContainer}>
			<img src={badgeFolderImagePath}></img>
			<span>{`${_('VelocityBadgeDesignerDirectory')}`}</span>
		</div>
	);

	return (
		<Modal
			keyboard={false}
			maskClosable={false}
			closable={true}
			onCancel={onCancel}
			onClickOk={() => null}
			width={700}
			title={title}
			visible={true}
			customZoomClass={styles.badgeModalDisplayContainer}
			className={styles.badgeModalContainer}
			maskStyle={maskStyle}
			footer={[footer]}
			closeIconStyle={closeIconStyle}>
			<Spin spinning={isLoading} tip={_('Loading')}>
				{content}
			</Spin>
		</Modal>
	);
};

export { BadgeDirectoryModal };
